import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import { UpdateCompanyComplianceSettingsForm } from '../../types';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../ui/button';

const defaultSettings: UpdateCompanyComplianceSettingsForm = {
  requireGeneralLiability: true,
  requireAuto: false,
  requireWorkersCompensation: true,
  requireUmbrella: false,
  requireProperty: false,
  requirePolicyVerification: false,
  requireW9Review: false,
  requireCodeOfConduct: false,
  requireSafetyAttestation: false,
};

interface CompanyComplianceSettingsPanelProps {
  standalone?: boolean;
}

const CompanyComplianceSettingsPanel: React.FC<CompanyComplianceSettingsPanelProps> = ({
  standalone = false,
}) => {
  const queryClient = useQueryClient();
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');
  const [form, setForm] = useState<UpdateCompanyComplianceSettingsForm>(defaultSettings);

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['company-compliance-settings'],
    queryFn: () => apiService.getCompanyComplianceSettings(),
    enabled: hasVendorCompliance,
  });

  useEffect(() => {
    if (settingsResponse?.success && settingsResponse.data) {
      const data = settingsResponse.data;
      setForm({
        requireGeneralLiability: data.requireGeneralLiability,
        requireAuto: data.requireAuto,
        requireWorkersCompensation: data.requireWorkersCompensation,
        requireUmbrella: data.requireUmbrella,
        requireProperty: data.requireProperty,
        minGeneralLiabilityLimit: data.minGeneralLiabilityLimit,
        minAutoLimit: data.minAutoLimit,
        minWorkersCompensationLimit: data.minWorkersCompensationLimit,
        minUmbrellaLimit: data.minUmbrellaLimit,
        minPropertyLimit: data.minPropertyLimit,
        requirePolicyVerification: data.requirePolicyVerification,
        requireW9Review: data.requireW9Review,
        requireCodeOfConduct: data.requireCodeOfConduct,
        requireSafetyAttestation: data.requireSafetyAttestation,
      });
    }
  }, [settingsResponse]);

  const saveMutation = useMutation({
    mutationFn: (data: UpdateCompanyComplianceSettingsForm) => apiService.updateCompanyComplianceSettings(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Compliance settings saved');
        queryClient.invalidateQueries({ queryKey: ['company-compliance-settings'] });
        queryClient.invalidateQueries({ queryKey: ['company-compliance-summary'] });
        queryClient.invalidateQueries({ queryKey: ['company-compliance-needs-attention'] });
        queryClient.invalidateQueries({ queryKey: ['vendor-compliance'] });
        queryClient.invalidateQueries({ queryKey: ['vendors'] });
        queryClient.invalidateQueries({ queryKey: ['vendor'] });
      } else {
        toast.error(response.message || 'Failed to save compliance settings');
      }
    },
    onError: () => toast.error('Failed to save compliance settings'),
  });

  if (!hasVendorCompliance) {
    return null;
  }

  const wrapperClass = standalone ? 'max-w-4xl' : 'max-w-4xl mt-8 scroll-mt-24';

  const policyRows = [
    { requireKey: 'requireGeneralLiability' as const, limitKey: 'minGeneralLiabilityLimit' as const, label: 'General Liability' },
    { requireKey: 'requireAuto' as const, limitKey: 'minAutoLimit' as const, label: 'Auto' },
    { requireKey: 'requireWorkersCompensation' as const, limitKey: 'minWorkersCompensationLimit' as const, label: 'Workers Compensation' },
    { requireKey: 'requireUmbrella' as const, limitKey: 'minUmbrellaLimit' as const, label: 'Umbrella' },
    { requireKey: 'requireProperty' as const, limitKey: 'minPropertyLimit' as const, label: 'Property' },
  ];

  const handleLimitChange = (field: keyof UpdateCompanyComplianceSettingsForm, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : Number(value),
    }));
  };

  return (
    <div id={standalone ? undefined : 'vendor-compliance-settings'} className={wrapperClass}>
      <div className="card">
        {!standalone && (
          <div className="card-header flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Vendor Compliance Requirements
          </div>
        )}
        <div className="card-body space-y-6">
          {isLoading ? (
            <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure required insurance types, minimum coverage limits, and document review rules for vendor compliance.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="py-2 pr-4">Policy Type</th>
                      <th className="py-2 pr-4">Required</th>
                      <th className="py-2">Minimum Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policyRows.map((row) => (
                      <tr key={row.requireKey} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-3 pr-4 font-medium">{row.label}</td>
                        <td className="py-3 pr-4">
                          <input
                            type="checkbox"
                            checked={form[row.requireKey]}
                            onChange={(e) => setForm((prev) => ({ ...prev, [row.requireKey]: e.target.checked }))}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="py-3">
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            value={form[row.limitKey] ?? ''}
                            onChange={(e) => handleLimitChange(row.limitKey, e.target.value)}
                            className="input w-40"
                            placeholder="No minimum"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requirePolicyVerification}
                    onChange={(e) => setForm((prev) => ({ ...prev, requirePolicyVerification: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Require COI document verification
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requireW9Review}
                    onChange={(e) => setForm((prev) => ({ ...prev, requireW9Review: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Require W-9 review before acceptance
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requireCodeOfConduct}
                    onChange={(e) => setForm((prev) => ({ ...prev, requireCodeOfConduct: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Require Code of Conduct
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requireSafetyAttestation}
                    onChange={(e) => setForm((prev) => ({ ...prev, requireSafetyAttestation: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Require Safety Attestation
                </label>
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => saveMutation.mutate(form)}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save Compliance Settings'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyComplianceSettingsPanel;
